<template lang="html">
	<form @submit.prevent="saveChanges" class="omapi-settings-page">
		<core-save-button
			v-if="!isLoading"
			size="large"
			color="green"
			class="omapi-settings-page__top-save"
			:saving="isSaving"
			:disabled="!changed"
		/>

		<slot name="tabs"></slot>

		<common-alerts id="om-settings-errors" :alerts="alerts"/>

		<settings-not-connected v-if="! connected"/>

		<common-card title="Miscellaneous Settings">
			<core-loading v-if="isLoading"></core-loading>
			<div v-else>
				<div class="omapi-card-setting-section" v-if="canAutoUpdate">
					<header>
						<h3>
							Automatic Updates
						</h3>
					</header>
					<div>
						<common-selectable-group
							groupName="auto_updates"
							:options="[
								{
									label: 'Yes (recommended)',
									value: 'all',
									help: 'Get the latest features, bug fixes, and security updates as they are released',
									checked: 'all' === auto_updates
								},
								{
									label: 'Minor Only',
									value: 'minor',
									help: 'Get bug fixes and security updates, but not major features',
									checked: 'minor' === auto_updates
								},
								{
									label: 'None',
									value: '',
									help: 'Manually update everything',
									checked: !auto_updates
								}
							]"
							@input="setValue"
						/>
					</div>
				</div>

				<div class="omapi-card-setting-section">
					<header>
						<h3>
							Hide Announcements?
						</h3>
					</header>
					<div>
						<p>
							Would you like to hide plugin announcements and update details?
						</p>

						<common-toggle-input onText="Yes" offText="No" v-model="hide_announcements"/>
					</div>
				</div>

				<div class="omapi-card-setting-section">
					<header>
						<h3>
							Enable Anonymous Usage Tracking?
						</h3>
					</header>
					<div>
						<p>
							By allowing us to track usage data we can better help you because we know which WordPress configurations, themes and plugins we should test.
						</p>

						<common-toggle-input onText="Yes" offText="No" v-model="usage_tracking"/>
					</div>
				</div>

				<div class="omapi-card-setting-section">
					<header>
					</header>
					<div>
						<core-save-button
							size="large"
							color="green"
							:saving="isSaving"
							:disabled="!changed"
						/>
					</div>
				</div>
			</div>
		</common-card>

	</form>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import { defaultState } from '@/settings/store/state'
import { bulkWatchHandler } from '@/components/utils'

const defaults    = defaultState()['miscSettings']
const settingKeys = Object.keys(defaults)

export default {
	data () {
		return {
			...this.getSettings(defaults),
			changed : false,
			orig    : false,
		}
	},
	beforeMount () {
		this.fetch()
	},
	watch : {
		...bulkWatchHandler(settingKeys, 'checkIfChanged'),
		miscSettings (newValue, oldVal) {
			if (newValue !== oldVal) {
				Object.keys(newValue).forEach(k => {
					this[k] = newValue[k]
				})
			}
		},
	},
	computed : {
		...mapState([ 'error', 'alerts' ]),
		...mapGetters('settings', [ 'miscSettings' ]),
		...mapGetters([ 'connected' ]),
		isLoading () {
			return this.$store.getters.isFetching('miscSettings') && !this.$store.getters.isFetched('miscSettings')
		},
		isSaving () {
			return this.$store.getters.isLoading('miscSettingsSave')
		},
		canAutoUpdate () {
			// TODO: Determine how this will work functionally.
			return false
		},
	},
	methods : {
		...mapActions('settings', [ 'fetchMiscSettings', 'saveMiscSettings' ]),
		saveChanges () {
			this.saveMiscSettings(this.getSettings(this))
				.then(() => this.resetOrig())
		},
		setValue (event) {
			let name = event.target.name
			let value = ''

			switch (event.target.type) {
			case 'checkbox' :
				// For toggles, 'checked' is 'off'
				value = !event.target.checked
				break
			case 'radio' :
				value = event.target.value
				break
			}

			this[name] = value
		},
		fetch () {
			if (!this.isLoading) {
				this.fetchMiscSettings()
					.then(() => this.resetOrig())
			}
		},
		resetOrig () {
			this.changed = false
			this.orig = { ...this.miscSettings }
		},
		checkIfChanged () {
			this.changed = Boolean(settingKeys.find(k => this[k] !== this.$get(`orig.${k}`)))
		},
		getSettings (allSettings) {
			const all = {}
			settingKeys.forEach(k => {
				all[k] = allSettings[k]
			})

			return all
		},
	},
}
</script>
<style lang="scss" scoped>
	.omapi-settings-page {
		position: relative;
		.omapi-settings-page__top-save {
			position: absolute;
			top: 12px;
			right: 0;
			margin-right: 0;
		}
	}
</style>
